/**
 * Resolve the media props the Composer passes to a presentation shell. The
 * Composer historically passed every first-media URL as `imageUrl`, so a
 * video-first post rendered its mp4 as a broken `<img>`. This keeps ordered
 * mixed children intact, routes known video URLs to `<video>`, and limits the
 * scalar `videoUrl` prop to a true single-video post.
 *
 * `media_type` is authoritative for a single extensionless video. A multi-item
 * set needs per-child media metadata to classify extensionless children; the
 * persisted `media_urls` shape does not currently carry that metadata, so this
 * resolver only classifies children whose URL declares a video type.
 */
export interface MediaPostShape {
  media_urls?: string[] | null;
  media_type?: string | null;
  cover_image_url?: string | null;
  media_keys?: {
    childManifest?: Array<{
      childId?: string;
      url?: string;
      sourceFingerprint?: string;
      renditions?: Record<string, Record<string, unknown>>;
    }>;
  } | null;
}

export interface ResolvedMediaProps {
  isVideoFirst: boolean;
  imageUrl: string | null;
  imageUrls: string[];
  mediaItems: Array<{
    url: string;
    kind: 'image' | 'video';
    videoPoster?: string | null;
    childId?: string;
    sourceFingerprint?: string;
    renditions?: Record<string, Record<string, unknown>>;
  }>;
  videoUrl: string | null;
  videoPoster: string | undefined;
}

const VIDEO_EXT = /\.(mp4|mov|webm|m4v)(\?|$)/i;

export function resolveMediaProps(post: MediaPostShape): ResolvedMediaProps {
  const urls = (post.media_urls ?? []).filter(
    (url): url is string => typeof url === 'string' && url.trim().length > 0,
  );
  const firstMediaUrl = urls[0] ?? null;
  const isVideoFirst =
    post.media_type === 'video' || (!!firstMediaUrl && VIDEO_EXT.test(firstMediaUrl));
  const childManifest = post.media_keys?.childManifest ?? [];
  const mediaItems = urls.map((url, index) => ({
    url,
    kind: (VIDEO_EXT.test(url) || (urls.length === 1 && isVideoFirst) ? 'video' : 'image') as
      'image' | 'video',
    videoPoster:
      VIDEO_EXT.test(url) && url === firstMediaUrl ? (post.cover_image_url ?? null) : null,
    ...(childManifest[index]?.childId ? { childId: childManifest[index].childId } : {}),
    ...(childManifest[index]?.sourceFingerprint
      ? { sourceFingerprint: childManifest[index].sourceFingerprint }
      : {}),
    ...(childManifest[index]?.renditions ? { renditions: childManifest[index].renditions } : {}),
  }));
  const imageItems = mediaItems.filter((item) => item.kind === 'image');
  const videoItems = mediaItems.filter((item) => item.kind === 'video');

  return {
    isVideoFirst,
    imageUrl: imageItems[0]?.url ?? null,
    imageUrls: imageItems.map((item) => item.url),
    mediaItems,
    videoUrl: mediaItems.length === 1 && videoItems[0] ? videoItems[0].url : null,
    videoPoster:
      mediaItems.length === 1 && videoItems[0]
        ? (videoItems[0].videoPoster ?? undefined)
        : undefined,
  };
}
